/**
 * ProductDocuments — Phase 4 PDF deliverable, upgraded for the PIP lean
 * rebuild (WS5.2). Compact summary with an expandable full document library.
 *
 * Data source:
 *   - canonical_products.pdfs jsonb, normalized server-side by
 *     lib/render-policy/display-pdfs getDisplayPdfs into DisplayPdf rows.
 *     Two entry generations feed it: Phase-4 hosted rows ({type,
 *     supabase_url, file_size_bytes, page_count, …}) and WS1-T4 discovered
 *     candidates ({url, anchor_text, status:'discovered'}).
 *   - Mode gating happens in the resolver: strict (production) renders
 *     hosted documents only; the resolver's dev preview mode also returns
 *     discovered ones flagged verified=false.
 *
 * FRIDGE-354 — what a reader sees is the document's name and nothing else.
 *   - The owner's directive: no "(unverified)" badges and no file sizes on the
 *     list. Both are gone, and so is the page count that sat beside the size.
 *   - The badge existed to label unverified documents; this component now
 *     lists none. "Unverified" here meant "found by search, never confirmed
 *     to be a live PDF" (scripts/pip-verify-pdfs is the confirmation), so a
 *     link to one is a link that may be dead or may be the wrong model's
 *     document. Production strict mode never passed one anyway; this filter
 *     makes the same rule hold at the component boundary in every
 *     environment, so nothing "unverified"-shaped can render again. Reviewing
 *     discovered candidates is a resolver/script concern, not a page one.
 *   - Byte counts and page counts stay in the MediaObject JSON-LD
 *     (components/seo/product-schemas.tsx), which is structured data for
 *     crawlers, not on-screen copy.
 *
 * Brand policy / Rule 4 ("everything displayed renders from Supabase"):
 *   - URLs are rewritten through fridge.com via getHostedPdfUrl, so no raw
 *     `*.supabase.co` host leaks. The /manuals rewrite serves the PDF body
 *     untouched while the visible URL stays on fridge.com.
 *
 * Real-or-Hidden Rule 3:
 *   - Empty/null docs → return null. Section absent from DOM.
 */

import Link from 'next/link'
import { FileText, Zap, Wrench, Shield, Ruler, BookOpen, ScrollText, ExternalLink, ListChecks } from 'lucide-react'
import { getHostedPdfUrl } from '@/lib/image-url'
import type { DisplayPdf } from '@/lib/render-policy/display-pdfs'

interface ProductDocumentsProps {
  docs: DisplayPdf[] | null | undefined
  className?: string
}

// Display name + icon per PDF type. Anything unrecognized falls back to
// "Document" with the generic FileText icon.
const PDF_TYPE_META: Record<
  string,
  { label: string; Icon: typeof FileText }
> = {
  spec_sheet: { label: 'Spec Sheet', Icon: FileText },
  owners_manual: { label: "Owner's Manual", Icon: BookOpen },
  energy_guide: { label: 'EnergyGuide', Icon: Zap },
  install_instructions: { label: 'Installation Instructions', Icon: Wrench },
  quick_start: { label: 'Quick Start Guide', Icon: ListChecks },
  warranty: { label: 'Warranty', Icon: Shield },
  dimension_drawing: { label: 'Dimension Drawing', Icon: Ruler },
  brochure: { label: 'Brochure', Icon: ScrollText },
}

/**
 * The rows that may render: verified only, and only with a resolvable href.
 * Exported so the count the header shows and the count a test expects come
 * from the one function.
 */
export function listableDocuments(docs: DisplayPdf[] | null | undefined): Array<DisplayPdf & { downloadUrl: string }> {
  const out: Array<DisplayPdf & { downloadUrl: string }> = []
  for (const doc of docs ?? []) {
    if (!doc || !doc.verified) continue
    // Hosted docs proxy through fridge.com/manuals/... (next.config.js
    // rewrite → Supabase Storage) so no raw *.supabase.co host leaks and the
    // PDF body is served untouched. If a hosted URL cannot be mapped to a
    // /manuals path, fall back to the resolver's href (already fronted in
    // strict mode) rather than drop the doc.
    const downloadUrl = doc.hosted ? (getHostedPdfUrl(doc.href) ?? doc.href) : doc.href
    if (!downloadUrl) continue
    out.push({ ...doc, downloadUrl })
  }
  return out
}

export function ProductDocuments({ docs, className = '' }: ProductDocumentsProps) {
  const listed = listableDocuments(docs)
  if (listed.length === 0) return null
  const officialCount = listed.filter(doc => doc.official).length

  // Preserve the compact one-per-type summary, with every remaining file
  // reachable through native keyboard-accessible disclosure.
  const summaryTypes = new Set<string>()
  const primary = listed.filter(doc => {
    if (summaryTypes.size >= 4 || summaryTypes.has(doc.type)) return false
    summaryTypes.add(doc.type)
    return true
  })
  const primaryUrls = new Set(primary.map(doc => doc.downloadUrl))
  const additional = listed.filter(doc => !primaryUrls.has(doc.downloadUrl))
  // A reviewed label names the exact file (FRIDGE-1181); otherwise the type
  // label stands. Only files that would print the same words are numbered.
  const nameOf = (doc: (typeof listed)[number]) => doc.label || PDF_TYPE_META[doc.type]?.label || 'Document'
  const nameCounts = new Map<string, number>()
  const labels = new Map<string, string>()
  for (const doc of listed) nameCounts.set(nameOf(doc), (nameCounts.get(nameOf(doc)) ?? 0) + 1)
  const namePositions = new Map<string, number>()
  for (const doc of listed) {
    const name = nameOf(doc)
    const position = (namePositions.get(name) ?? 0) + 1
    namePositions.set(name, position)
    labels.set(doc.downloadUrl, (nameCounts.get(name) ?? 0) > 1 ? `${name} ${position}` : name)
  }

  const renderDocuments = (documents: typeof listed) => (
      <ul className="divide-y divide-gray-100" role="list">
        {documents.map((doc, idx) => {
          const meta = PDF_TYPE_META[doc.type] || { label: 'Document', Icon: FileText }
          const Icon = meta.Icon
          return (
            <li key={`${doc.type}-${idx}`} className="first:pt-0 last:pb-0">
              <Link
                href={doc.downloadUrl}
                // FRIDGE-1505: a PDF is not a page; the router's prefetch of it
                // (?_rsc=) answered 400 from the storage proxy.
                prefetch={false}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 group rounded-lg px-3 py-2.5 -mx-3 hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40"
                data-pdf-type={doc.type}
                {...(doc.official ? { 'data-document-source': 'manufacturer' } : {})}
              >
                <span className="w-9 h-9 rounded-lg bg-[#0469f0]/10 flex items-center justify-center text-[#0469f0] flex-shrink-0">
                  <Icon className="w-4 h-4" aria-hidden="true" />
                </span>
                {/* FRIDGE-1181: the maker's own file, linked under the tracked
                    official-link exception, says so in words, under the label
                    in the label's own column, so it never narrows the label
                    at phone width. */}
                <span className="flex-1 min-w-0">
                  <span className="block truncate text-sm font-semibold text-gray-900 group-hover:text-[#0469f0] transition-colors">
                    {labels.get(doc.downloadUrl)}
                  </span>
                  {doc.official && (
                    <span className="block text-[11px] font-semibold leading-4 text-gray-700">Manufacturer site</span>
                  )}
                </span>
                <span className="sr-only">
                  {doc.official ? ' (PDF on the manufacturer’s website, opens in a new tab)' : ' (PDF, opens in a new tab)'}
                </span>
                <ExternalLink
                  className="w-4 h-4 text-gray-500 group-hover:text-[#0469f0] transition-colors flex-shrink-0"
                  aria-hidden="true"
                />
              </Link>
            </li>
          )
        })}
      </ul>
  )

  return (
    <section
      className={`rounded-lg border border-gray-200 bg-white p-4 sm:p-5 ${className}`}
      aria-label="Manuals and guides"
      data-testid="product-documents"
      data-pdf-count={listed.length}
    >
      <header className="mb-3">
        <p className="text-[#ff9951] font-bold tracking-widest text-[11px] uppercase mb-1.5">
          Documentation
        </p>
        <div className="flex items-center gap-2">
          <h2 className="text-xl md:text-2xl font-black tracking-tighter text-gray-900">Manuals &amp; Guides</h2>
          <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">
            {listed.length}
          </span>
        </div>
      </header>
      <p className="text-sm text-gray-500 leading-relaxed mb-3">
        Manuals, specifications, installation guides, and other documents
        available for this product.
      </p>

      {renderDocuments(primary)}
      {additional.length > 0 && (
        <details className="mt-3 border-t border-gray-200 pt-2" data-testid="additional-product-documents">
          <summary className="min-h-11 cursor-pointer rounded-lg py-3 text-sm font-semibold text-[#0469f0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40">
            More documents ({additional.length})
          </summary>
          {renderDocuments(additional)}
        </details>
      )}

      <p className="mt-4 text-xs text-gray-500">
        {officialCount === 0
          ? 'Manufacturer documents archived by Fridge.com so links keep working when manufacturer URLs change.'
          : officialCount === listed.length
            ? 'These documents open on the manufacturer’s own website.'
            : 'Documents marked Manufacturer site open on the maker’s own website; the others are archived by Fridge.com so their links keep working.'}
      </p>
    </section>
  )
}

export default ProductDocuments
